import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { InfoCircle, SearchNormal1, TickCircle, Trontron } from "iconsax-reactjs";
import { cn } from "lib/utils";
import { fetchCoinNetworks } from "../../api/fetchers";
import { BaseIcon, EthIcon, PolygonIcon } from "../../asset/network-bank-icons";
import TRC20 from "asset/icons/TRC20.svg";
import BEP20 from "asset/icons/BEP20.svg";
import CELO from "asset/icons/CELO.svg";
import SOL from "asset/icons/SOL.svg";

export default function SelectNetworkModal({
    isOpen,
    onClose,
    onSelect,
    selectedId,
    currency,
    businessId,
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [selected, setSelected] = useState(selectedId ?? null);
    const [networks, setNetworks] = useState([]);
    const [loading, setLoading] = useState(false);

    const pickIcon = (n) => {
        const key = String(n?.id || n?.name || n?.shortName || n?.label || "").toLowerCase();

        if (n?.icon && React.isValidElement(n.icon)) {
            return React.cloneElement(n.icon, { className: "h-6 w-6" });
        }
        if (typeof n?.icon === "string" && n.icon) {
            return <img src={n.icon} alt="" className="h-6 w-6" />;
        }

        if (key.includes("erc") || key.includes("ethereum") || key.includes("eth"))
            return <EthIcon className="h-5 w-5" />;
        if (key.includes("trc") || key.includes("tron"))
            return <img src={TRC20} alt="TRC20" className="h-6 w-6" />;
        if (key.includes("bep") || key.includes("bsc") || key.includes("bnb"))
            return <img src={BEP20} alt="BEP20" className="h-6 w-6" />;
        if (key.includes("polygon") || key.includes("matic"))
            return <PolygonIcon className="h-5 w-5" />;
        if (key.includes("base")) return <BaseIcon className="h-6 w-6" />;
        if (key.includes("celo")) return <img src={CELO} alt="Celo" className="h-6 w-6" />;
        if (key.includes("sol") || key.includes("solana"))
            return <img src={SOL} alt="Solana" className="h-6 w-6" />;

        return <EthIcon className="h-6 w-6" />;
    };

    useEffect(() => {
        if (!isOpen || !currency || !businessId) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await fetchCoinNetworks({ currency, businessId });
                if (!cancelled) setNetworks(Array.isArray(list) ? list : []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, currency, businessId]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            return;
        }
        setSelected(selectedId ?? null);
    }, [isOpen, selectedId]);

    const filtered = useMemo(() => {
        const q = (searchTerm || "").toLowerCase();
        return networks.filter((n) => {
            const name = (n.name || "").toLowerCase();
            const alt = (n.shortName || n.label || n.id || "").toLowerCase();
            return name.includes(q) || alt.includes(q);
        });
    }, [networks, searchTerm]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[999] bg-black/30 flex items-center justify-center">
            <div className="bg-white rounded-2xl w-full max-w-xl p-6 relative">
                <button className="absolute right-4 top-4" onClick={onClose}>
                    <X size={20} />
                </button>

                <h2 className="text-xl font-semibold mb-4">Change network</h2>

                <div className="bg-[#F1F3FF] text-sm text-[#4B4E68] flex gap-2 items-center rounded-lg px-4 py-2 mb-3">
                    <InfoCircle size="22" color="#2C53F2" variant="Bold" />
                    Select the correct network for the recipient’s wallet.
                </div>

                <div className="relative w-full mb-3">
                    <SearchNormal1
                        size="20"
                        color="#6B6F8C"
                        className="pointer-events-none absolute !left-5 top-1/2 -translate-y-1/2"
                    />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search networks"
                        className="w-full !h-12 bg-[#EFF0FA] !rounded-lg !pl-12 !pr-4
                       !text-[14px] text-[#6B6F8C] placeholder:text-[#6B6F8C]
                       outline-none border-0 focus:outline-none focus:ring-0"
                    />
                </div>

                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {loading && (
                        <p className="text-sm text-gray-500 text-center py-6">Loading networks…</p>
                    )}

                    {!loading &&
                        filtered.map((net) => {
                            const feeText =
                                net.feeText ??
                                net.fee_label ??
                                (net.free === true || net.fee === 0 || net.fee === "0"
                                    ? "Free"
                                    : undefined);

                            return (
                                <button
                                    key={net.id || net.name}
                                    onClick={() => {
                                        setSelected(net.id);
                                        onSelect?.(net);
                                        onClose();
                                    }}
                                    className={cn(
                                        "w-full flex items-center justify-between px-3 py-3 border rounded-xl text-left transition",
                                        selected === net.id
                                            ? "border-[#6F00FF] border-solid border-1 shadow-sm"
                                            : "border-gray-200"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        {/* Icon bubble */}
                                        <span className="h-10 w-10 rounded-full bg-[#F2F3FC] grid place-items-center">
                                            {pickIcon(net)}
                                        </span>

                                        <div>
                                            <p className="font-medium">{net.name}</p>
                                            <p className="text-sm text-gray-500">{net.label}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <TickCircle
                                            className={
                                                selected === net.id
                                                    ? "text-[#6F00FF]"
                                                    : "text-transparent"
                                            }
                                            variant={selected === net.id ? "Bold" : "Outline"}
                                            size={20}
                                        />
                                    </div>
                                </button>
                            );
                        })}

                    {!loading && filtered.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-6">
                            No networks match your search.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
